import { useEffect, useState } from 'react';
import { Tablet } from 'lucide-react';
import { BanepadCourtScreen } from './BanepadCourtScreen';
import { Padel24Engine } from '../../engine/platformEngine';
import { americanoMatchToSlots } from '../../engine/americanoResults';
import { playEndSound } from '../../lib/sound';
import type { BanepadPadCallbacks } from '../../state/useBanepadPadActions';
import { computePadZoom } from './deviceFill';
import { BanepadContactSheet } from './BanepadContactSheet';

export interface BanepadDeviceDisplayProps {
  /** Court this device is scoped to — comes from the resolved device token, NOT the URL. */
  courtId: number;
  /** Court name from the registration (used only for the small device indicator). */
  courtName?: string;
  /** The centre that owns the courts — recipient of «Kontakt senteret». */
  clubName?: string;
  /** Per-court metadata (display name / sponsor) — same shape Sentralen uses. */
  courtMeta?: any;
  bookings?: any[];
  tournament?: any;
  activeSeries?: any;
  activities?: any[];
  plannedMatches?: any[];
  allPlayers?: any[];
  /** Live clock from the parent (drives the pad). Falls back to an internal clock if omitted. */
  now?: Date;
  /** Force flags from the resolved court context — make the pad show a forced/auto-forced session. */
  forceTournament?: boolean;
  forceSeries?: boolean;
  forceSingleMatch?: any;
  /**
   * Court-scoped result/live-score callbacks (from buildBanepadPadCallbacks) so
   * the pad's buttons write back exactly like Sentralen's preview. Omitted in
   * isolated renders/tests → the pad's controls are inert.
   */
  callbacks?: Partial<BanepadPadCallbacks>;
  /**
   * What this pad's result submission is doing, so the button that started it
   * can say so (#216). Omitted ⇒ the pad's buttons behave as before.
   */
  submitStatus?: { phase: 'idle' | 'sending' | 'done'; matchId: string | null };
  /** Server-computed facts for a pad holding only its own court (#185). */
  projected?: any;
}

const noop = () => {};

/** The automatic device scale (see computePadZoom) — tracks the viewport. */
export function usePadZoom(): number {
  const read = () =>
    typeof window === 'undefined' ? 1 : computePadZoom(window.innerWidth, window.innerHeight);
  const [zoom, setZoom] = useState(read);
  useEffect(() => {
    const onResize = () => setZoom(read());
    onResize();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return zoom;
}

/**
 * Device-side adapter that renders the EXISTING Banepad pad
 * (<BanepadCourtScreen>) — the very same component Sentralen uses for a court's
 * full-size pad and stacked preview — scoped to the court resolved from the
 * device token. No new pad UI is built here; this is the thin seam between the
 * device session and the shared renderer (the data is supplied by the
 * store-connected BanepadDeviceShell).
 *
 * Court identity comes ONLY from `courtId` (resolved from the token). The URL
 * never chooses the court or the display mode. `preview` is set so the pad shows
 * no operator exit control; admin chrome (force lists, rutenett, auto-force
 * config) lives in BanepadCentralView and is simply not rendered here.
 */
export function BanepadDeviceDisplay({
  courtId,
  courtName,
  clubName,
  courtMeta = {},
  bookings = [],
  tournament = null,
  activeSeries = null,
  activities = [],
  plannedMatches = [],
  allPlayers = [],
  now: nowProp,
  forceTournament = false,
  forceSeries = false,
  forceSingleMatch = null,
  projected = null,
  callbacks,
  submitStatus,
}: BanepadDeviceDisplayProps) {
  // Use the parent's clock when provided (so it matches the resolved force
  // context); otherwise tick our own (standalone renders / tests).
  const [ownNow, setOwnNow] = useState(() => new Date());
  useEffect(() => {
    if (nowProp) return undefined;
    const id = setInterval(() => setOwnNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [nowProp]);
  const now = nowProp ?? ownNow;
  const zoom = usePadZoom();

  return (
    // Kiosk fit: the pad screen is a fixed 4:3 box whose content scales with its
    // WIDTH (cqw units). Cap the width to `min(100vw, 100dvh * 4/3)` and centre it
    // so the WHOLE pad always fits the tablet — both dimensions — with no
    // scrolling (logo, full 24h timeline incl. hour 23, and "Ingen flere
    // bookinger" all visible). The dark letterbox blends with the pad's theme.
    // `zoom` is applied ONCE here and inherited by every fill frame below
    // (they are position:fixed and would multiply if each zoomed itself).
    // <main>, NOT a div: on the pad this component IS the page — it mounts
    // from `main.tsx` above the portal shell, so AppShell's own <main> and skip
    // link are nowhere in the tree and the kiosk had no landmark at all (WCAG
    // 1.3.1). `main` is display:block by default and the class sets `flex`, so
    // nothing about the kiosk's layout or sizing moves.
    <main
      className="relative flex h-dvh w-full items-center justify-center overflow-hidden"
      style={{ background: '#0a0b10', zoom }}
      data-pad-zoom={zoom}
    >
      <div className="w-full" style={{ maxWidth: 'min(100vw, calc(100dvh * 4 / 3))' }}>
        <BanepadCourtScreen
          court={courtId}
          courtMeta={courtMeta}
          bookings={bookings}
          tournament={tournament}
          activeSeries={activeSeries}
          activities={activities}
          plannedMatches={plannedMatches}
          allPlayers={allPlayers}
          now={now}
          preview
          fill
          forceTournament={forceTournament}
          forceSeries={forceSeries}
          forceSingleMatch={forceSingleMatch}
          projected={projected}
          americanoMatchToSlots={americanoMatchToSlots}
          Padel24Engine={Padel24Engine}
          playEndSound={playEndSound}
          onRecordResult={callbacks?.onRecordResult ?? noop}
          onPlayerClick={noop}
          onRecordKMResult={callbacks?.onRecordKMResult ?? noop}
          onRecordKMLiveScore={callbacks?.onRecordKMLiveScore ?? noop}
          onRecordPadLiveScore={callbacks?.onRecordPadLiveScore ?? noop}
          onRecordSerieResult={callbacks?.onRecordSerieResult ?? noop}
          onRecordSerieLiveScore={callbacks?.onRecordSerieLiveScore ?? noop}
          onRecordSingleMatchResult={callbacks?.onRecordSingleMatchResult ?? noop}
          submitStatus={submitStatus}
          onLiveSingleMatchScore={callbacks?.onLiveSingleMatchScore ?? noop}
          onOpenTournamentView={noop}
          onOpenSeriesView={noop}
        />
      </div>

      {/* «Kontakt senteret» — the court writes to the centre (lights out, net, …). */}
      <BanepadContactSheet courtId={courtId} courtName={courtName} clubName={clubName} />

      {/* Small, non-invasive device/court indicator (demo/debug) — not admin UI. */}
      <div
        className="fixed bottom-2 left-2 flex items-center gap-1 rounded-full px-2 py-0.5 pointer-events-none"
        style={{ zIndex: 50, background: 'rgba(15,23,42,0.55)', color: '#e2e8f0', fontSize: 10 }}
      >
        <Tablet size={10} /> BanePad · {courtName ?? `Bane ${courtId}`}
      </div>
    </main>
  );
}
